/**
 * PhotoViewer Component
 * 
 * Full-screen photo viewer with likes, comments, and actions.
 */

import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  View,
  Text,
  Image,
  Modal,
  TouchableOpacity,
  ScrollView,
  TextInput,
  KeyboardAvoidingView,
  Platform,
  ActivityIndicator,
  Alert,
  Dimensions,
  Keyboard,
  InputAccessoryView,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColors, useTheme } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";
import { usePhotoLikes } from "../hooks/usePhotoLikes";
import { usePhotoComments } from "../hooks/usePhotoComments";
import { usePhotoDownload } from "../hooks/usePhotoDownload";
import type { PhotoWithRelations } from "../types";

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get("window");

interface PhotoViewerProps {
  visible: boolean;
  photo: PhotoWithRelations | null;
  onClose: () => void;
  onDelete?: (photoId: string) => void;
  onToggleVisibility?: (photoId: string, visible: boolean) => void;
  onToggleFeatured?: (photoId: string, featured: boolean) => void;
}

/**
 * Full-screen photo viewer
 */
export const PhotoViewer: React.FC<PhotoViewerProps> = ({
  visible,
  photo,
  onClose,
  onDelete,
  onToggleVisibility,
  onToggleFeatured,
}) => {
  const colors = useThemeColors();
  const { isDark } = useTheme();
  const insets = useSafeAreaInsets();
  const { user, activeProfile, roles } = useAuth();
  const userId = activeProfile?.id || user?.id;
  const scrollViewRef = useRef<ScrollView>(null);

  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [showActions, setShowActions] = useState(false);
  const [keyboardVisible, setKeyboardVisible] = useState(false);

  // Track keyboard visibility for iOS
  useEffect(() => {
    const showSubscription = Keyboard.addListener(
      Platform.OS === "ios" ? "keyboardWillShow" : "keyboardDidShow",
      () => {
        setKeyboardVisible(true);
        setTimeout(() => {
          scrollViewRef.current?.scrollToEnd({ animated: true });
        }, 100);
      }
    );
    const hideSubscription = Keyboard.addListener(
      Platform.OS === "ios" ? "keyboardWillHide" : "keyboardDidHide",
      () => {
        setKeyboardVisible(false);
      }
    );

    return () => {
      showSubscription.remove();
      hideSubscription.remove();
    };
  }, []);

  // Hooks
  const { isLiked, likesCount, toggleLike } = usePhotoLikes(photo?.id || "");
  const { comments, isSending, addComment, deleteComment } = usePhotoComments(photo?.id || "");
  const { downloadPhoto, isDownloading } = usePhotoDownload();

  const isOwner = photo?.uploaded_by === userId;
  const canModerate = roles.isSuperAdmin;

  /**
   * Handle like toggle
   */
  const handleLike = useCallback(async () => {
    await toggleLike();
  }, [toggleLike]);

  /**
   * Handle download
   */
  const handleDownload = useCallback(async () => {
    if (!photo) return;
    await downloadPhoto(photo);
  }, [photo, downloadPhoto]);

  /**
   * Handle add comment
   */
  const handleAddComment = useCallback(async () => {
    if (!newComment.trim()) return;
    Keyboard.dismiss();
    const success = await addComment(newComment);
    if (success) {
      setNewComment("");
    }
  }, [newComment, addComment]);

  /**
   * Handle delete comment
   */
  const handleDeleteComment = useCallback((commentId: string) => {
    Alert.alert(
      "Reactie verwijderen",
      "Weet je zeker dat je deze reactie wilt verwijderen?",
      [
        { text: "Annuleren", style: "cancel" },
        {
          text: "Verwijderen",
          style: "destructive",
          onPress: () => deleteComment(commentId),
        },
      ]
    );
  }, [deleteComment]);

  /**
   * Handle delete photo
   */
  const handleDelete = useCallback(() => {
    if (!photo) return;
    Alert.alert(
      "Foto verwijderen",
      "Weet je zeker dat je deze foto wilt verwijderen? Dit kan niet ongedaan worden gemaakt.",
      [
        { text: "Annuleren", style: "cancel" },
        {
          text: "Verwijderen",
          style: "destructive",
          onPress: () => {
            onDelete?.(photo.id);
            onClose();
          },
        },
      ]
    );
  }, [photo, onDelete, onClose]);

  /**
   * Handle visibility toggle
   */
  const handleToggleVisibility = useCallback(() => {
    if (!photo || !onToggleVisibility) return;
    onToggleVisibility(photo.id, !photo.is_visible);
    setShowActions(false);
  }, [photo, onToggleVisibility]);

  /**
   * Handle featured toggle
   */
  const handleToggleFeatured = useCallback(() => {
    if (!photo || !onToggleFeatured) return;
    onToggleFeatured(photo.id, !photo.is_featured);
    setShowActions(false);
  }, [photo, onToggleFeatured]);

  /**
   * Handle comment input focus - scroll to bottom
   */
  const handleCommentFocus = () => {
    setTimeout(() => {
      scrollViewRef.current?.scrollToEnd({ animated: true });
    }, 300);
  };

  /**
   * Toggle comments section
   */
  const handleToggleComments = () => {
    const newShowComments = !showComments;
    setShowComments(newShowComments);
    if (newShowComments) {
      setTimeout(() => {
        scrollViewRef.current?.scrollToEnd({ animated: true });
      }, 100);
    }
  };

  if (!photo) return null;

  const uploaderName = photo.uploader?.nickname || photo.uploader?.full_name || "Onbekend";
  const uploadDate = new Date(photo.created_at).toLocaleDateString("nl-NL", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="overFullScreen"
      onRequestClose={onClose}
    >
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : undefined}
        style={{ flex: 1, backgroundColor: "#000" }}
        keyboardVerticalOffset={Platform.OS === "ios" ? 10 : 0}
      >
        {/* Header - fixed at top */}
        <View
          className="flex-row items-center justify-between px-4"
          style={{ 
            paddingTop: insets.top + 8, 
            paddingBottom: 12,
            backgroundColor: "rgba(0,0,0,0.8)",
          }}
        >
          <TouchableOpacity
            onPress={onClose}
            className="p-2"
            accessibilityRole="button"
            accessibilityLabel="Sluiten"
          >
            <Ionicons name="close" size={28} color="#FFFFFF" />
          </TouchableOpacity>

          <View className="flex-row items-center">
            {/* Download */}
            <TouchableOpacity
              onPress={handleDownload}
              disabled={isDownloading}
              className="p-2 mr-2"
              accessibilityRole="button"
              accessibilityLabel="Downloaden"
            >
              {isDownloading ? (
                <ActivityIndicator size="small" color="#FFFFFF" />
              ) : (
                <Ionicons name="download-outline" size={24} color="#FFFFFF" />
              )}
            </TouchableOpacity>

            {/* More actions */}
            {(isOwner || canModerate) && (
              <TouchableOpacity
                onPress={() => setShowActions(true)}
                className="p-2"
                accessibilityRole="button"
                accessibilityLabel="Meer opties"
              >
                <Ionicons name="ellipsis-horizontal" size={24} color="#FFFFFF" />
              </TouchableOpacity>
            )}
          </View>
        </View>

        {/* Scrollable content */}
        <ScrollView
          ref={scrollViewRef}
          className="flex-1"
          contentContainerStyle={{ flexGrow: 1 }}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="interactive"
          automaticallyAdjustKeyboardInsets={Platform.OS === "ios"}
        >
          {/* Image */}
          <View className="items-center justify-center">
            <Image
              source={{ uri: photo.file_url }}
              style={{
                width: SCREEN_WIDTH,
                height: SCREEN_HEIGHT * 0.55,
              }}
              resizeMode="contain"
            />
          </View>

          {/* Footer content */}
          <View
            style={{ 
              backgroundColor: "rgba(0,0,0,0.8)",
              paddingBottom: showComments ? 0 : insets.bottom + 8,
            }}
          >
            {/* Actions row */}
            <View className="flex-row items-center px-4 py-3">
              {/* Like */}
              <TouchableOpacity
                onPress={handleLike}
                className="flex-row items-center mr-6"
                accessibilityRole="button"
                accessibilityLabel={isLiked ? "Niet meer leuk vinden" : "Leuk vinden"}
              >
                <Ionicons
                  name={isLiked ? "heart" : "heart-outline"}
                  size={28}
                  color={isLiked ? "#EF4444" : "#FFFFFF"}
                />
                {likesCount > 0 && (
                  <Text className="ml-1 text-white font-medium">{likesCount}</Text>
                )}
              </TouchableOpacity>

              {/* Comments */}
              <TouchableOpacity
                onPress={handleToggleComments}
                className="flex-row items-center"
                accessibilityRole="button"
                accessibilityLabel="Reacties bekijken"
              >
                <Ionicons
                  name={showComments ? "chatbubble" : "chatbubble-outline"}
                  size={26}
                  color="#FFFFFF"
                />
                {comments.length > 0 && (
                  <Text className="ml-1 text-white font-medium">{comments.length}</Text>
                )}
              </TouchableOpacity>

              {/* Spacer */}
              <View className="flex-1" />

              {/* Badges */}
              {photo.is_featured && (
                <View className="flex-row items-center mr-2 px-2 py-1 rounded-full bg-amber-500">
                  <Ionicons name="star" size={14} color="#FFFFFF" />
                  <Text className="ml-1 text-xs text-white font-medium">Uitgelicht</Text>
                </View>
              )}
            </View>

            {/* Uploader info */}
            <View className="px-4 pb-3">
              <Text className="text-white font-semibold">{uploaderName}</Text>
              {photo.description && (
                <Text className="text-gray-300 mt-1">{photo.description}</Text>
              )}
              <Text className="text-gray-400 text-xs mt-1">{uploadDate}</Text>
            </View>

            {/* Comments section */}
            {showComments && (
              <View className="border-t border-gray-700">
                {/* Comments list */}
                <View className="px-4 py-2 max-h-40">
                  {comments.length === 0 ? (
                    <Text className="text-gray-400 text-center py-4">
                      Nog geen reacties
                    </Text>
                  ) : (
                    comments.map((comment) => {
                      const commentUser = comment.user?.nickname || comment.user?.full_name || "Onbekend";
                      const isOwnComment = comment.user_id === userId;
                      return (
                        <View key={comment.id} className="flex-row py-2">
                          <View className="flex-1">
                            <View className="flex-row items-center">
                              <Text className="text-white font-medium mr-2">
                                {commentUser}
                              </Text>
                              {comment.is_edited && (
                                <Text className="text-gray-500 text-xs">(bewerkt)</Text>
                              )}
                            </View>
                            <Text className="text-gray-300 mt-0.5">
                              {comment.content}
                            </Text>
                          </View>
                          {(isOwnComment || canModerate) && (
                            <TouchableOpacity
                              onPress={() => handleDeleteComment(comment.id)}
                              className="p-2"
                            >
                              <Ionicons name="trash-outline" size={16} color="#EF4444" />
                            </TouchableOpacity>
                          )}
                        </View>
                      );
                    })
                  )}
                </View>

                {/* Comment input */}
                <View 
                  className="flex-row items-center px-4 py-3 border-t border-gray-700"
                  style={{ paddingBottom: insets.bottom + 12 }}
                >
                  <TextInput
                    value={newComment}
                    onChangeText={setNewComment}
                    onFocus={handleCommentFocus}
                    placeholder="Schrijf een reactie..."
                    placeholderTextColor="#9CA3AF"
                    className="flex-1 text-white py-2 px-4 rounded-full mr-2"
                    style={{ 
                      backgroundColor: "rgba(255,255,255,0.1)",
                      minHeight: 40,
                    }}
                    multiline
                    maxLength={500}
                  />
                  <TouchableOpacity
                    onPress={handleAddComment}
                    disabled={!newComment.trim() || isSending}
                    className="p-2"
                  >
                    {isSending ? (
                      <ActivityIndicator size="small" color={colors.primary} />
                    ) : (
                      <Ionicons
                        name="send"
                        size={24}
                        color={newComment.trim() ? colors.primary : "#6B7280"}
                      />
                    )}
                  </TouchableOpacity>
                </View>
              </View>
            )}
          </View>
        </ScrollView>

        {/* Actions modal */}
        <Modal
          visible={showActions}
          transparent
          animationType="fade"
          onRequestClose={() => setShowActions(false)}
        >
          <TouchableOpacity
            className="flex-1 justify-end"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
            activeOpacity={1}
            onPress={() => setShowActions(false)}
          >
            <View
              className="rounded-t-3xl"
              style={{
                backgroundColor: colors.surface,
                paddingBottom: insets.bottom + 16,
              }}
            >
              <View className="items-center py-4">
                <View
                  className="w-10 h-1 rounded-full"
                  style={{ backgroundColor: colors.border }}
                />
              </View>

              {/* Super admin actions */}
              {canModerate && (
                <>
                  <TouchableOpacity
                    onPress={handleToggleFeatured}
                    className="flex-row items-center px-6 py-4"
                  >
                    <Ionicons
                      name={photo.is_featured ? "star" : "star-outline"}
                      size={24}
                      color="#F59E0B"
                    />
                    <Text className="ml-4" style={{ color: colors.text }}>
                      {photo.is_featured ? "Niet meer uitlichten" : "Uitlichten"}
                    </Text>
                  </TouchableOpacity>

                  <TouchableOpacity
                    onPress={handleToggleVisibility}
                    className="flex-row items-center px-6 py-4"
                  >
                    <Ionicons
                      name={photo.is_visible ? "eye-off-outline" : "eye-outline"}
                      size={24}
                      color={colors.textSecondary}
                    />
                    <Text className="ml-4" style={{ color: colors.text }}>
                      {photo.is_visible ? "Verbergen" : "Zichtbaar maken"}
                    </Text>
                  </TouchableOpacity>
                </>
              )}

              {/* Delete */}
              {(isOwner || canModerate) && (
                <TouchableOpacity
                  onPress={handleDelete}
                  className="flex-row items-center px-6 py-4"
                >
                  <Ionicons name="trash-outline" size={24} color="#EF4444" />
                  <Text className="ml-4 text-red-500">Verwijderen</Text>
                </TouchableOpacity>
              )}

              {/* Cancel */}
              <TouchableOpacity
                onPress={() => setShowActions(false)}
                className="mx-4 mt-2 py-3 rounded-xl items-center"
                style={{ backgroundColor: colors.surfaceSecondary }}
              >
                <Text style={{ color: colors.textSecondary }}>Annuleren</Text>
              </TouchableOpacity>
            </View>
          </TouchableOpacity>
        </Modal>
      </KeyboardAvoidingView>
    </Modal>
  );
};

export default PhotoViewer;
